import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronRight, AlertCircle, AlertTriangle } from 'lucide-react';
import IconHighlight from '../../../../../components/IconHighlight';
import { handleKeyboardClick, truncateText } from '../../../../../utils';
import {
  ACTIVITY_TYPE_CONFIG,
  ALERT_TYPES,
  METADATA_ICONS,
  METADATA_BAR_CONFIG,
  ACTIVITY_CARD_MAX_CHARACTERS,
} from '../../constants';

/**
 * Single activity list item. Expanded shows description + metadata on hover (CSS group).
 * Compressed shows only icon, title, alert (if any), time ago, chevron.
 */
const ActivityListItem = ({
  item,
  isCompressed = false,
  onItemClick = undefined,
}) => {
  const typeConfig = ACTIVITY_TYPE_CONFIG[item.type] ?? ACTIVITY_TYPE_CONFIG.call;
  const hasAlerts = item.alerts?.length > 0;
  const alertTooltip = useMemo(
    () => (hasAlerts ? item.alerts.map((a) => a.message).join('\n') : ''),
    [hasAlerts, item.alerts],
  );

  const metadataKeys = METADATA_BAR_CONFIG[item.type] ?? METADATA_BAR_CONFIG.call;

  const visibleMetadataItems = useMemo(() => {
    const m = item.metadata;
    return metadataKeys.filter((key) => {
      const val = m?.[key];
      if (key === 'userId') return !!val;
      if (key === 'sentiment') return val != null;
      if (key === 'assignedTasks') return val > 0;
      if (key === 'hasRecording' || key === 'hasExternalLink' || key === 'flagged') return !!val;
      if (key === 'direction') return !!val && item.type === 'call';
      if (key === 'duration') return !!val;
      if (key === 'commentsCount' || key === 'attachmentsCount' || key === 'linkCount') return val > 0;
      return false;
    });
  }, [item.metadata, item.type, metadataKeys]);

  const hasMetadata = visibleMetadataItems.length > 0;

  const handleClick = useCallback(() => {
    onItemClick?.(item);
  }, [item, onItemClick]);

  const containerClass = [
    'bg-surface-muted/50 border border-edge rounded-lg flex flex-col gap-2 w-full cursor-pointer hover:bg-brand-50/90 dark:hover:bg-brand-950/25 transition-colors',
    isCompressed ? 'p-2' : 'p-3',
    !isCompressed ? 'group' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      data-compressed={isCompressed || undefined}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      className={containerClass}
      onKeyDown={(event) => handleKeyboardClick(event, handleClick)}
    >
      {/* Main row */}
      <div className="flex items-center justify-between gap-1 w-full">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <IconHighlight
            icon={typeConfig.icon}
            bgColor={typeConfig.bgColor}
            iconColor={typeConfig.iconColor}
          />
          <div className={`flex-1 min-w-0 ${isCompressed ? 'overflow-hidden' : ''}`}>
            <span
              className={[
                'text-xs leading-5 font-medium text-content',
                isCompressed ? 'block overflow-hidden text-ellipsis whitespace-nowrap' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {truncateText(item.title, ACTIVITY_CARD_MAX_CHARACTERS)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {hasAlerts && (
            <span title={alertTooltip} className="flex items-center justify-center">
              {item.alerts.some((a) => a.type === ALERT_TYPES.EXPIRED)
                ? <AlertCircle size={15} className="text-red-500" />
                : <AlertTriangle size={15} className="text-amber-500" />}
            </span>
          )}
          <span className="text-xs font-medium text-content-muted">{item.timeAgo}</span>
          <button
            type="button"
            aria-label="View details"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            className="inline-flex items-center justify-center w-7 h-7 rounded text-content-faint hover:bg-surface-hover transition-colors"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Description row (expanded only) */}
      {!isCompressed && (item.description || item.author) && (
        <div className="flex items-start gap-0.5 text-xs leading-5 text-content-muted w-full min-w-0">
          {item.author && (
            <span className="text-xs leading-5 text-content flex-shrink-0 mr-0.5">{item.author}</span>
          )}
          {item.description && (
            <div className="flex-1 min-w-0">
              <span className="text-xs leading-5 text-content-muted">
                {truncateText(item.description, ACTIVITY_CARD_MAX_CHARACTERS)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Metadata bar — revealed on hover via Tailwind group */}
      {!isCompressed && hasMetadata && (
        <div className="w-fit bg-surface-muted/70 border border-edge rounded-lg flex items-center flex-wrap max-h-0 overflow-hidden opacity-0 transition-all duration-200 ease-out group-hover:max-h-[50px] group-hover:opacity-100 group-hover:py-1 group-hover:px-2 group-hover:border">
          {visibleMetadataItems.map((key, idx) => {
            const m = item.metadata;
            const MetaIcon = METADATA_ICONS[key] ?? METADATA_ICONS[key.replace(/Count$/, 's')];
            const separator = idx < visibleMetadataItems.length - 1;

            return (
              <div
                key={key}
                className={[
                  'flex items-center gap-1',
                  separator ? 'after:content-["·"] after:mx-2 after:text-base after:leading-6 after:font-normal after:text-content-faint' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {key === 'userId' && (
                  <span className="text-xs font-medium text-content-muted">{m.userId}</span>
                )}
                {key === 'sentiment' && MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                {key === 'assignedTasks' && (
                  <>
                    {MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                    <span className="text-xs font-medium text-content-muted">{m.assignedTasks}</span>
                  </>
                )}
                {key === 'hasRecording' && MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                {key === 'hasExternalLink' && MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                {key === 'duration' && (
                  <>
                    {MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                    <span className="text-xs font-medium text-content-muted">{m.duration}</span>
                  </>
                )}
                {key === 'direction' && MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                {key === 'flagged' && MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                {key === 'commentsCount' && (
                  <>
                    {MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                    <span className="text-xs font-medium text-content-muted">{m.commentsCount}</span>
                  </>
                )}
                {key === 'attachmentsCount' && (
                  <>
                    {MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                    <span className="text-xs font-medium text-content-muted">{m.attachmentsCount}</span>
                  </>
                )}
                {key === 'linkCount' && (
                  <>
                    {MetaIcon && <MetaIcon size={14} className="text-content-muted" />}
                    <span className="text-xs font-medium text-content-muted">{m.linkCount}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

ActivityListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    author: PropTypes.string,
    timeAgo: PropTypes.string,
    alerts: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        message: PropTypes.string,
      }),
    ),
    metadata: PropTypes.object,
  }).isRequired,
  isCompressed: PropTypes.bool,
  onItemClick: PropTypes.func,
};


export default React.memo(ActivityListItem);
